import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface StudentFormData {
  prefix: 'ด.ช.' | 'ด.ญ.'
  fullName: string
  nickname: string
  studentNumber: string
  note: string
}

export function StudentFormPage() {
  const navigate = useNavigate()
  const { studentId } = useParams()
  const isEdit = !!studentId

  const [form, setForm] = useState<StudentFormData>({
    prefix: 'ด.ช.',
    fullName: '',
    nickname: '',
    studentNumber: '',
    note: '',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof StudentFormData, string>>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  // Load existing student for edit mode
  useEffect(() => {
    if (!studentId) return
    fetch(apiUrl(`/api/students/${studentId}`), { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          const s = res.data
          setForm({
            prefix: s.prefix,
            fullName: s.fullName,
            nickname: s.nickname || '',
            studentNumber: String(s.studentNumber),
            note: s.note || '',
          })
        }
      })
      .catch(() => {})
  }, [studentId])

  function validate(): boolean {
    const errs: typeof errors = {}

    if (!form.fullName.trim()) {
      errs.fullName = 'กรุณากรอกชื่อ-นามสกุล'
    } else if (!/^[\u0E00-\u0E7F\s\-\.]{2,100}$/.test(form.fullName.trim())) {
      errs.fullName = 'กรุณากรอกชื่อภาษาไทย'
    }

    if (!form.nickname.trim()) {
      errs.nickname = 'กรุณากรอกชื่อเล่น'
    }

    const num = parseInt(form.studentNumber, 10)
    if (!form.studentNumber || isNaN(num)) {
      errs.studentNumber = 'กรุณากรอกเลขที่'
    } else if (num < 1 || num > 60) {
      errs.studentNumber = 'เลขที่ต้องอยู่ระหว่าง 1-60'
    }

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)
    const payload = {
      prefix: form.prefix,
      fullName: form.fullName.trim(),
      nickname: form.nickname.trim() || undefined,
      studentNumber: parseInt(form.studentNumber, 10),
      note: form.note.trim() || undefined,
    }

    try {
      const url = isEdit ? apiUrl(`/api/students/${studentId}`) : apiUrl('/api/students')
      const method = isEdit ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()

      if (data.success) {
        setToast(isEdit ? 'บันทึกสำเร็จ' : 'เพิ่มนักเรียนสำเร็จ')
        setTimeout(() => navigate('/students'), 800)
      } else {
        const field = data.error?.field
        if (field && field in form) {
          setErrors({ [field]: data.error.messageTh || data.error.message })
        } else {
          setToast(data.error?.messageTh || 'เกิดข้อผิดพลาด')
        }
      }
    } catch {
      setToast('เกิดข้อผิดพลาด กรุณาลองใหม่')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-600">
            ⟨
          </button>
          <h1 className="text-lg font-bold text-gray-900">
            {isEdit ? 'แก้ไขนักเรียน' : 'เพิ่มนักเรียน'}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Prefix */}
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700">คำนำหน้า</legend>
            <div className="flex gap-4">
              {(['ด.ช.', 'ด.ญ.'] as const).map((p) => (
                <label
                  key={p}
                  className={`flex flex-1 cursor-pointer items-center justify-center rounded-lg border-2 py-3 text-sm font-medium transition-colors ${
                    form.prefix === p
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 bg-white text-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="prefix"
                    value={p}
                    checked={form.prefix === p}
                    onChange={() => setForm({ ...form, prefix: p })}
                    className="sr-only"
                  />
                  {p}
                </label>
              ))}
            </div>
          </fieldset>

          {/* Full Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              ชื่อ-นามสกุล <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              placeholder="กรุณากรอกชื่อ-นามสกุลภาษาไทย"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
            />
            {errors.fullName && (
              <p className="mt-1 text-xs text-red-500">{errors.fullName}</p>
            )}
          </div>

          {/* Nickname */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              ชื่อเล่น <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
              placeholder="ชื่อเล่น"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm ${
                errors.nickname ? 'border-red-300 bg-red-50' : 'border-gray-200'
              } focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
            />
            {errors.nickname && (
              <p className="mt-1 text-xs text-red-500">{errors.nickname}</p>
            )}
          </div>

          {/* Student Number */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              เลขที่ <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={60}
                value={form.studentNumber}
                onChange={(e) => setForm({ ...form, studentNumber: e.target.value })}
                placeholder=""
                className={`w-20 rounded-lg border px-3 py-2.5 text-center text-sm ${
                  errors.studentNumber ? 'border-red-300 bg-red-50' : 'border-gray-200'
                } focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              <span className="text-sm text-gray-400">(1–60)</span>
            </div>
            {errors.studentNumber && (
              <p className="mt-1 text-xs text-red-500">{errors.studentNumber}</p>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              หมายเหตุ <span className="text-gray-400">(ไม่บังคับ)</span>
            </label>
            <textarea
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={2}
              maxLength={200}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50"
          >
            {isSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </form>
      </main>

      {/* Toast */}
      {toast && (
        <div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      <BottomTabBar active="students" />
    </div>
  )
}
